import type { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import { TENANT_ACCESS_ERROR_CODES } from '@org/contracts';

import type { TenantGrantStore } from '@/auth/tenant-access/grant-store';
import {
  apiPathFor,
  isGrantRoute,
  isNoRetryRequest,
  isPlatformWriteRequest,
  tenantIdFromPath,
} from '@/auth/tenant-access/tenant-path';

// Cross-tenant access grant interceptors (ADR-0023 §1, §3).
//
// - Request: adds `X-Helix-Tenant-Access` only to our own api (same origin
//   and base path as `VITE_API_BASE_URL`), only on `/v1/tenants/{id}/...`,
//   and only when the store holds a live grant for that `{id}`. Any other
//   value is removed, so nothing else can send a grant.
// - Creating a clinic (`POST /v1/tenants`) and refreshing the IDIS2GO
//   customer list (`POST /v1/idis2go-customers/discovery-runs`) have no
//   `{id}`. Each is a write in the caller's own tenant (the platform), so it
//   takes the platform grant, and a 403 on it asks for step-up for the
//   platform.
// - Response: on `403 TENANT_ACCESS_REQUIRED`, asks the step-up handler for
//   a grant and repeats the request once. Concurrent 403s for one tenant
//   share one step-up. Secret-creating POSTs are never repeated: the admin
//   gets the dialog, then presses the button again.
//
// The token is never logged and never put in an error message. Every
// rejected error leaves here without the grant header and without the
// bearer token in `error.config.headers`, so code that logs or keeps an
// axios error can't leak them. A retry still works: the request
// interceptors set both headers again.

/** Header name as the browser sends it. The api reads it lower case. */
export const TENANT_ACCESS_HEADER_NAME = 'X-Helix-Tenant-Access';

/** Opens the step-up dialog. Resolves true once a grant is in the store. */
export type StepUpHandler = (tenantId: string) => Promise<boolean>;

interface TenantAccessDeps {
  store: TenantGrantStore;
  stepUp: StepUpHandler;
  /** `VITE_API_BASE_URL`. A grant is never sent anywhere else. */
  apiBaseUrl: string;
  /** The signed-in user's own tenant (the platform for a System Admin), or null. */
  platformTenantId: () => string | null;
}

interface StepUpConfig extends InternalAxiosRequestConfig {
  __tenantAccessTried?: boolean;
}

export function installTenantAccessInterceptors(
  client: AxiosInstance,
  deps: TenantAccessDeps,
): void {
  const pending = new Map<string, Promise<boolean>>();

  function stepUpOnce(tenantId: string): Promise<boolean> {
    const existing = pending.get(tenantId);
    if (existing !== undefined) {
      return existing;
    }
    const next = deps
      .stepUp(tenantId)
      .catch(() => {
        return false;
      })
      .finally(() => {
        pending.delete(tenantId);
      });
    pending.set(tenantId, next);
    return next;
  }

  function apiPath(config: InternalAxiosRequestConfig): string | null {
    return apiPathFor(client.getUri(config), deps.apiBaseUrl);
  }

  /** The tenant whose grant this request takes, or null for none. */
  function targetTenant(method: string | undefined, path: string | null): string | null {
    if (isGrantRoute(path)) {
      return null;
    }
    if (isPlatformWriteRequest(method, path)) {
      const platform = deps.platformTenantId();
      return platform === null ? null : platform.toLowerCase();
    }
    return tenantIdFromPath(path);
  }

  client.interceptors.request.use((config: StepUpConfig) => {
    config.headers.delete(TENANT_ACCESS_HEADER_NAME);
    const tenantId = targetTenant(config.method, apiPath(config));
    if (tenantId === null) {
      return config;
    }
    const token = deps.store.tokenFor(tenantId);
    if (token !== null) {
      config.headers.set(TENANT_ACCESS_HEADER_NAME, token);
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    async (error: unknown) => {
      const retried = await retryAfterStepUp(error);
      if (retried !== null) {
        // The retry passed through these interceptors itself, so an error
        // from it is already scrubbed.
        return retried;
      }
      // Pass the original rejection on unchanged, whatever its type, as
      // axios does.
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
      return Promise.reject(scrubCredentials(error));
    },
  );

  /**
   * On `403 TENANT_ACCESS_REQUIRED`, asks for step-up and repeats the request
   * once. Resolves to the retry's response, or null when the original error
   * should be rejected. A failed retry throws its own (scrubbed) error.
   */
  async function retryAfterStepUp(error: unknown): Promise<AxiosResponse | null> {
    if (!isAxiosErrorWithConfig(error) || !isTenantAccessRequired(error)) {
      return null;
    }
    const config = error.config as StepUpConfig;
    const path = apiPath(config);
    const tenantId = targetTenant(config.method, path);
    if (tenantId === null || config.__tenantAccessTried === true) {
      return null;
    }
    config.__tenantAccessTried = true;

    // The server refused the grant we sent (revoked, role removed, or
    // expired by its clock). Drop it so the dialog asks for a new one.
    // Only drop it if it is still the current one: a concurrent step-up
    // may already have stored a fresh grant.
    const sent = config.headers.get(TENANT_ACCESS_HEADER_NAME);
    if (typeof sent === 'string' && sent === deps.store.tokenFor(tenantId)) {
      deps.store.remove(tenantId, 'rejected');
    }

    const granted = await stepUpOnce(tenantId);
    if (!granted) {
      return null;
    }
    if (isNoRetryRequest(config.method, path)) {
      return null;
    }
    return client.request(config);
  }
}

/** Removes credentials from a rejected error's request config. */
function scrubCredentials(error: unknown): unknown {
  if (isAxiosErrorWithConfig(error)) {
    error.config.headers.delete(TENANT_ACCESS_HEADER_NAME);
    error.config.headers.delete('Authorization');
  }
  return error;
}

function isAxiosErrorWithConfig(
  error: unknown,
): error is AxiosError & { config: InternalAxiosRequestConfig } {
  const config = (error as { config?: { headers?: unknown } } | null)?.config;
  const headers = config?.headers as { delete?: unknown } | undefined;
  return typeof headers?.delete === 'function';
}

function isTenantAccessRequired(error: AxiosError): boolean {
  if (error.response?.status !== 403) {
    return false;
  }
  const data = error.response.data as { code?: unknown } | undefined;
  return data?.code === TENANT_ACCESS_ERROR_CODES.required;
}
